/**
 * GovernancePage Component
 * AIDLC Administrative Backend
 *
 * Module 02: Governance & Compliance
 * Two tabs: "Rules" (existing CRUD) and "Enforcement" (live enforcement log + post-checks).
 * Adding and switching rules is for Admins (GOV-WRITE-1); everyone else reads the rules and a note saying so.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import {
  getGovernanceRules,
  updateGovernanceRule,
  createGovernanceRule,
  type GovernanceRule,
} from '../../services/governanceService';
import { GovernanceEnforcementLog } from './GovernanceEnforcementLog';
import { useAuth } from '../../hooks/useAuth';

/** Shown in place of "New Rule" to anyone who is not an Admin (GOV-WRITE-1). */
export const RULES_ADMIN_NOTE = 'Only an Admin can add a rule or turn one on or off.';
/** The empty list's line for anyone who is not an Admin: they have no button to add one (GOV-WRITE-1). */
export const RULES_EMPTY_NON_ADMIN = 'An Admin can add one.';

const ruleTypeColors: Record<string, 'info' | 'success' | 'warning' | 'danger'> = {
  'quality-gate': 'info',
  'sop': 'success',
  'approval-workflow': 'warning',
  'security-policy': 'danger',
};

const ruleTypeLabels: Record<string, string> = {
  'quality-gate': 'Quality Gate',
  'sop': 'SOP',
  'approval-workflow': 'Approval Workflow',
  'security-policy': 'Security Policy',
};

export function GovernancePage() {
  const [activeTab, setActiveTab] = useState<'rules' | 'enforcement'>('rules');
  const [rules, setRules] = useState<GovernanceRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<GovernanceRule | null>(null);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState('quality-gate');
  const [newAppliesTo, setNewAppliesTo] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const data = await getGovernanceRules();
        setRules(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load governance rules');
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const filteredRules = useMemo(() => {
    if (filterType === 'all') return rules;
    return rules.filter((r) => r.type === filterType);
  }, [rules, filterType]);

  const stats = useMemo(() => ({
    total: rules.length,
    active: rules.filter((r) => r.isActive).length,
    qualityGates: rules.filter((r) => r.type === 'quality-gate').length,
    securityPolicies: rules.filter((r) => r.type === 'security-policy').length,
  }), [rules]);

  const handleToggleActive = async (rule: GovernanceRule) => {
    try {
      const updated = await updateGovernanceRule(rule.id, { isActive: !rule.isActive });
      setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update rule');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      const created = await createGovernanceRule({
        name: newName,
        description: newDescription,
        type: newType,
        isActive: true,
        config: {},
        appliesTo: newAppliesTo.split('\n').filter((c) => c.trim()),
        priority: 10,
        createdBy: 'Neo',
      });
      setRules((prev) => [...prev, created]);
      setShowCreateModal(false);
      setNewName('');
      setNewDescription('');
      setNewAppliesTo('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rule');
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading governance rules..." />;

  return (
    <div>
      {/* Tab Switcher */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'rules'
              ? 'border-ldaja-blue text-ldaja-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Rules
        </button>
        <button
          onClick={() => setActiveTab('enforcement')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'enforcement'
              ? 'border-ldaja-blue text-ldaja-blue'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Enforcement
        </button>
      </div>

      {/* Enforcement Tab */}
      {activeTab === 'enforcement' && <GovernanceEnforcementLog />}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-ldaja-black">{stats.total}</div>
              <div className="text-xs text-gray-500 mt-1">Total Rules</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-xs text-gray-500 mt-1">Active</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-ldaja-blue">{stats.qualityGates}</div>
              <div className="text-xs text-gray-500 mt-1">Quality Gates</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{stats.securityPolicies}</div>
              <div className="text-xs text-gray-500 mt-1">Security Policies</div>
            </div>
          </div>

          {/* Toolbar */}
          <div className={`flex items-center justify-between ${isAdmin ? 'mb-6' : 'mb-2'}`}>
            <div className="flex items-center gap-2">
              {['all', 'quality-gate', 'sop', 'approval-workflow', 'security-policy'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    filterType === type
                      ? 'bg-ldaja-blue text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {type === 'all' ? 'All' : ruleTypeLabels[type] || type}
                </button>
              ))}
            </div>
            {isAdmin && (
              <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                + New Rule
              </Button>
            )}
          </div>
          {/* On its own line, as on the Enforcement tab, so it wraps at phone width */}
          {!isAdmin && <p className="text-xs text-gray-500 mb-6">{RULES_ADMIN_NOTE}</p>}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4" role="alert">
              <p className="text-red-700 text-xs">{error}</p>
              <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
            </div>
          )}

          {/* Rules List */}
          {filteredRules.length === 0 ? (
            <EmptyState
              title="No governance rules found"
              description={isAdmin
                ? 'Create governance rules to enforce quality gates, SOPs, and security policies across the AIDLC system.'
                : RULES_EMPTY_NON_ADMIN}
              action={isAdmin ? (
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                  Create First Rule
                </Button>
              ) : undefined}
            />
          ) : (
            <div className="space-y-3">
              {filteredRules.map((rule) => (
                <div
                  key={rule.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => setSelectedRule(rule)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-ldaja-black">{rule.name}</h3>
                        <Badge variant={ruleTypeColors[rule.type] || 'info'}>
                          {ruleTypeLabels[rule.type] || rule.type}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{rule.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-400">
                        <span>Priority: {rule.priority}</span>
                        <span>{(rule.appliesTo || []).length} applies to</span>
                        <span>By {rule.createdBy}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Active toggle */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(rule); }}
                        disabled={!isAdmin}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          rule.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        title={!isAdmin ? RULES_ADMIN_NOTE : rule.isActive ? 'Active -- click to disable' : 'Disabled -- click to activate'}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            rule.isActive ? 'translate-x-4' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span className={`text-[10px] font-medium ${rule.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                        {rule.isActive ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rule Detail Modal */}
          <Modal
            isOpen={!!selectedRule}
            onClose={() => setSelectedRule(null)}
            title={selectedRule?.name || 'Rule Details'}
            size="md"
          >
            {selectedRule && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant={ruleTypeColors[selectedRule.type] || 'info'}>
                    {ruleTypeLabels[selectedRule.type] || selectedRule.type}
                  </Badge>
                  <Badge variant={selectedRule.isActive ? 'success' : 'neutral'} dot>
                    {selectedRule.isActive ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">{selectedRule.description}</p>

                {(selectedRule.appliesTo || []).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Applies To</h4>
                    <ul className="space-y-1">
                      {selectedRule.appliesTo.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                          <svg className="w-3.5 h-3.5 text-ldaja-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedRule.config && Object.keys(selectedRule.config).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Configuration</h4>
                    <pre className="text-xs text-gray-600 bg-gray-50 rounded p-3 overflow-x-auto">
                      {JSON.stringify(selectedRule.config, null, 2)}
                    </pre>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-[10px] text-gray-400">Priority</p>
                    <p className="text-xs font-medium text-gray-700">{selectedRule.priority}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Created By</p>
                    <p className="text-xs font-medium text-gray-700">{selectedRule.createdBy}</p>
                  </div>
                </div>

                <div className="text-[10px] text-gray-400 pt-2 border-t border-gray-100">
                  Created {new Date(selectedRule.createdAt).toLocaleDateString()} | Updated {new Date(selectedRule.updatedAt).toLocaleDateString()}
                </div>
              </div>
            )}
          </Modal>

          {/* Create Rule Modal */}
          <Modal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            title="Create Governance Rule"
            size="md"
            footer={
              <>
                <Button variant="secondary" size="sm" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreate}
                  isLoading={isCreating}
                  disabled={!newName.trim()}
                >
                  Create Rule
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                  placeholder="e.g., Code Review Required"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                  placeholder="What does this rule enforce?"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                >
                  <option value="quality-gate">Quality Gate</option>
                  <option value="sop">Standard Operating Procedure</option>
                  <option value="approval-workflow">Approval Workflow</option>
                  <option value="security-policy">Security Policy</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Applies To (one per line)</label>
                <textarea
                  value={newAppliesTo}
                  onChange={(e) => setNewAppliesTo(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 font-mono"
                  placeholder="All code changes&#10;Pull requests&#10;Agent deployments"
                />
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
